import { DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Workbook } from 'exceljs';
import { ComplianceRenewalFrequencyModel } from 'src/app/models/compliance/compliance-renewal-frequency.model';
import { CoursePersonByStateModel } from 'src/app/models/person-states/course-person-by-state.model';
import { PersonModel } from 'src/app/models/person/person.model';
import { TableColumn } from 'src/app/models/table-column.model';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { CourseTemplateService } from 'src/app/services/course-template.service';
import { CourseService } from 'src/app/services/course.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OpenPersonService } from 'src/app/services/open-person.service';
import { ConfirmationDialogData } from '../shared/confirmation-dialog/confirmation-dialog.component';

export type PersonTab =
  'completedPersons' | 'startedPersons' | 
  'openPersons' | 'pendingParticipants' | 'removedPersons';

export interface PersonTableRowModel {
  id: string;
  lastName: string;
  firstName: string;
  email: string;
  locationName?: string;
  date?: Date;            
  daysUntilOpen?: number; 
  reminderCount?: number | null;
  raw?: unknown;          
  managers?: PersonModel[]; 
}

@Component({
  selector: 'app-course-persons-table',
  templateUrl: './course-persons-table.component.html',
  styleUrls: ['./course-persons-table.component.scss'],
  standalone: false
})
export class CoursePersonsTableComponent implements OnInit {
  @Input() courseTemplateId!: string;
  @Input() isDeployed = false;
  @Input() trainingType?: TrainingTypeModel;
  @Input() complianceCategoryId?: string | null;
  @Input() renewalFrequency?: ComplianceRenewalFrequencyModel | null;
  @Input() isClosed = false;
  @Input() hasReminders = false;

  selectedTab!: PersonTab;
  personsTabs: { id: number; text: string; key: PersonTab }[] = [];

  columns: TableColumn[] = [];
  persons: PersonTableRowModel[] = [];

  @Input()
  personsByState: CoursePersonByStateModel | null = null;

  showCompleteCourseModal: boolean = false;
  startedPersonId: string | null = null;

  showRemovePersonModal: boolean = false;
  personIdToRemove: string | null = null;
  removePersonOptions: ConfirmationDialogData | null = null;
  
  get isCompliance(): boolean {
    return !!this.complianceCategoryId;
  }

  constructor(
    private courseTemplateService: CourseTemplateService,
    private courseService: CourseService,
    private loadingService: LoadingService,
    private openPersonService: OpenPersonService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.initTabs();
    this.selectedTab = this.personsTabs[0].key;

    if(this.personsByState) {
      this.applyTab(this.selectedTab);
    } else {
      this.loadPersons();
    }
  }

  private loadPersons(): void {
    this.loadingService
      .load(this.courseTemplateService.getCourseTemplatePersons(this.courseTemplateId))
      .subscribe((personsByState) => {
        this.personsByState = personsByState;
        this.applyTab(this.selectedTab);
      });
  }

  initTabs(): void {
    this.personsTabs = [
      {
        id: 0,
        text: this.translateService.instant(
          'ADMINISTRATION.PROCESSING-PROGRESS-STATUSES.CLOSED'
        ),
        key: 'completedPersons',
      },
      {
        id: 1,
        text: this.translateService.instant(
          'ADMINISTRATION.PROCESSING-PROGRESS-STATUSES.IN-PROGRESS'
        ),
        key: 'startedPersons',
      },
    ];

    if (this.isDeployed || this.isClosed) {
      this.personsTabs.push({
        id: 2,
        text: this.translateService.instant(
          'ADMINISTRATION.PROCESSING-PROGRESS-STATUSES.OPEN'
        ),
        key: 'openPersons',
      });
    }

    if (
      this.trainingType === TrainingTypeModel.Compliance &&
      this.complianceCategoryId &&
      !this.isClosed &&
      this.renewalFrequency !== ComplianceRenewalFrequencyModel.Never
    ) {
      this.personsTabs.push({
        id: 3,
        text: this.translateService.instant(
          'ADMINISTRATION.PROCESSING-PROGRESS-STATUSES.NOT-DUE'
        ),
        key: 'pendingParticipants',
      });
    }

    this.personsTabs.push({
      id: 4,
      text: this.translateService.instant(
        'ADMINISTRATION.PROCESSING-PROGRESS-STATUSES.REMOVED'
      ),
      key: 'removedPersons',
    });
  }

  onTabChange(tab: PersonTab): void {
    this.selectedTab = tab;
    this.applyTab(tab);
  }

  private applyTab(tab: PersonTab): void {
    if (!this.personsByState) {
      return;
    }

    this.persons = this.mapPersons(tab, this.personsByState);
    this.columns = this.getColumns(tab, true);
  }
  
  private getColumns(
    tab: PersonTab, includeActions = false
  ): TableColumn[] {
    let columns: TableColumn[];

    switch (tab) {
      case 'completedPersons':
        columns = [
          ...this.baseColumns,
          this.createDateColumn('COMMON.FIELDS.COMPLETED-DATE'),
          this.managerColumn,
        ];
        break;

      case 'startedPersons':
        columns = [
          ...this.baseColumns,
          this.createDateColumn('COMMON.FIELDS.STARTED-DATE'),
          this.managerColumn,
          ...(this.hasReminders ? [this.reminderCountColumn] : [])
        ];
        break;

      case 'openPersons':
        columns = [
          ...this.baseColumns,
          this.managerColumn,
          ...(this.hasReminders ? [this.reminderCountColumn] : []),
        ];
        break;

      case 'pendingParticipants':
        columns = [
          ...this.baseColumns,
          this.createDateColumn('COMMON.FIELDS.COMPLETED-DATE'),
          this.managerColumn,
          this.daysUntilOpenColumn
        ];
        break;

      case 'removedPersons':
        columns = [
          ...this.baseColumns,
          this.createDateColumn('COMMON.FIELDS.REMOVED-DATE'),
          this.managerColumn,
        ];
        break;

      default:
        columns = [];
    }

    return includeActions
      ? [...columns, this.actionsColumn]
      : columns;
  }

  private mapPersons(tab: PersonTab, data: CoursePersonByStateModel): PersonTableRowModel[] {
    switch (tab) {
      case 'completedPersons':
        return data.completedPersons.map((p) => ({
          id: p.person.id,
          lastName: p.person.lastName,
          firstName: p.person.firstName,
          email: p.person.email,
          locationName: p.person.location?.name,
          date: p.completedAt,
          managers: p.managers,
          raw: p,
        }));

      case 'startedPersons':
        return data.startedPersons.map((p) => ({
          id: p.person.id,
          lastName: p.person.lastName,
          firstName: p.person.firstName,
          email: p.person.email,
          locationName: p.person.location?.name,
          date: p.startedAt,
          reminderCount: p.reminderCount,
          managers: p.managers,
          raw: p,
        }));

      case 'openPersons':
        return data.openPersons.map((p) => ({
          id: p.person.id,
          lastName: p.person.lastName,
          firstName: p.person.firstName,
          email: p.person.email,
          locationName: p.person.location?.name,
          reminderCount: p.reminderCount,
          managers: p.managers,
          raw: p,
        }));

      case 'removedPersons':
        return data.removedPersons.map((p) => ({
          id: p.person.id,
          lastName: p.person.lastName,
          firstName: p.person.firstName,
          email: p.person.email,
          locationName: p.person.location?.name,
          date: p.removedAt,
          managers: p.managers,
          raw: p,
        }));

      case 'pendingParticipants':
        return data.pendingParticipants.map((p) => {
          return {
            id: p.person.id,
            lastName: p.person.lastName,
            firstName: p.person.firstName,
            email: p.person.email,
            locationName: p.person.location?.name,
            date: new Date(p.lastCompletedDate),
            daysUntilOpen: p.daysUntilOpen,
            managers: p.managers,
            raw: p,
          };
        });

      default:
        return [];
    }
  }

  private readonly baseColumns: TableColumn[] = [
    {
      dataField: 'lastName',
      caption: this.translateService.instant('COMMON.FIELDS.LAST-NAME'),
      cssClass: 'width-200',
    },
    {
      dataField: 'firstName',
      caption: this.translateService.instant('COMMON.FIELDS.FIRST-NAME'),
      cssClass: 'width-200',
    },
    {
      dataField: 'email',
      caption: this.translateService.instant('COMMON.FIELDS.EMAIL'),
      cssClass: 'width-150',
    },
    {
      dataField: 'locationName',
      caption: this.translateService.instant('COMMON.FIELDS.LOCATION'),
      cssClass: 'width-150',
    },
  ];

  private createDateColumn(translationKey: string): TableColumn {
    return {
      dataField: 'date',
      caption: this.translateService.instant(translationKey),
      cellValue: (data: any): any => {
        return new Date(data.date);
      },
      calculateSortValue: (data: PersonTableRowModel): Date | string => {
        if (!data.date) {
          return '';
        }

        return new Date(data.date);
      },
      calculateDisplayValue: (row: PersonTableRowModel): string => {
         if (!row.date) {
          return '';
        }

        return (
          new DatePipe(this.translateService.currentLang).transform(
            new Date(row.date),
            'dd.MM.yyyy'
          ) || ''
        );
      },
      cssClass: 'width-150',
    };
  }

  private readonly managerColumn: TableColumn = {
    dataField: 'managers',
    caption: this.translateService.instant('COMMON.FIELDS.MANAGERS'),
    cellValue: (data: PersonTableRowModel): string => {
      return data.managers
        ?.map(manager => `${manager.lastName} ${manager.firstName}`)
        .join(', ') || '';
    },
    calculateDisplayValue: (data: PersonTableRowModel): string => {
      return data.managers
        ?.map(manager => `${manager.lastName} ${manager.firstName}`)
        .join(', ') || '';
    },
    cssClass: 'width-150',
  };

  private readonly daysUntilOpenColumn: TableColumn = {
    dataField: 'daysUntilOpen',
    caption: this.translateService.instant('COMMON.FIELDS.DAYS-UNTIL-OPEN'),
    cssClass: 'width-100',
  };

  private readonly reminderCountColumn: TableColumn = {
    dataField: 'reminderCount',
    caption: this.translateService.instant('COMMON.FIELDS.REMINDER-COUNT'),
    cssClass: 'width-100',
  };

  private readonly actionsColumn: TableColumn = {
    dataField: 'this',
    caption: '',
    cellType: 'action',
    disableFiltering: true,
    cellTemplate: 'actionsTemplate',
    actions: [
      {
        icon: 'check',
        class: 'green-icon',
        hint: this.translateService.instant('MANAGE-ELEARNING.DETAIL.MARK-AS-COMPLETED'),
        isShowAction: () => this.selectedTab === 'startedPersons' && !this.isClosed,
        onClick: (row: PersonTableRowModel) => this.onCompleteCourseClick(row.id),
      },
      {
        icon: 'close',
        class: 'red-icon',
        isShowAction: () => this.selectedTab === 'openPersons' && !this.isClosed,
        onClick: (row: PersonTableRowModel) => this.onRemoveOpenPersonClick(row.id),
      },
      {
        icon: 'undo',
        hint: this.translateService.instant('MANAGE-ELEARNING.DETAIL.RETURN-TO-OPEN'),
        class: 'info-icon',
        isShowAction: () => this.selectedTab === 'removedPersons' && !this.isClosed,
        onClick: (row: PersonTableRowModel) => this.addOpenPerson(row.id),
      },
    ],
  };

  onRemoveOpenPersonClick(personId: string) {
    this.removePersonOptions = {
      submitButtonText: this.translateService.instant('COMMON.DELETE'),
      message: this.translateService.instant(
        'MANAGE-ELEARNING.DETAIL.REMOVE-OPEN-PERSON-DIALOG.TEXT'
      ),
      title: this.translateService.instant(
        'MANAGE-ELEARNING.DETAIL.REMOVE-OPEN-PERSON-DIALOG.TITLE'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
      hideUndo: true,
    };
    this.personIdToRemove = personId;
    this.showRemovePersonModal = true;
  } 

  removeOpenPerson(): void {
    if(!this.personIdToRemove) 
      return;

    this.loadingService
      .load(
        this.openPersonService.removeOpenPerson(
          this.courseTemplateId, this.personIdToRemove
        )
      )
      .subscribe(() => {
        this.showRemovePersonModal = false;
        this.loadPersons();
      });
  }

  addOpenPerson(personId: string) {
    this.loadingService
      .load(
        this.openPersonService.addOpenPerson(
          this.courseTemplateId, personId
        )
      )
      .subscribe(() => {
        this.loadPersons();
      });
  }

  onCompleteCourseClick(personId: string) {
    this.startedPersonId = personId;
    this.showCompleteCourseModal = true;
  } 

  completeCourseForStartedPerson(personId: string): void {
    this.loadingService
      .load(this.courseService.completeCourseForPerson(this.courseTemplateId, personId))
      .subscribe(() => this.loadPersons());
  }

  completeCourse(): void {
    if(!this.startedPersonId) return;
    this.showCompleteCourseModal = false;
    this.completeCourseForStartedPerson(this.startedPersonId);
  }

  public exportAllPersons(
    workbook: Workbook,
    model: CoursePersonByStateModel
  ): void {
    this.personsTabs.forEach(tab => {
      const persons = this.mapPersons(tab.key, model);

      const worksheet = workbook.addWorksheet(
        this.getSafeSheetName(tab.text)
      );

      const columns = this.getColumns(tab.key);

      const headerRow = worksheet.addRow(
        columns.map(column => column.caption || column.dataField || '')
      );

      headerRow.eachCell(cell => {
        cell.font = { bold: true };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center'
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      worksheet.views = [{ state: 'frozen', ySplit: 1 }];

      worksheet.autoFilter = {
        from: 'A1',
        to: `${this.getColumnLetter(columns.length)}1`
      };

      persons.forEach(person => {
        worksheet.addRow(
          columns.map(column =>
            this.getCellValueForExport(person, column)
          )
        );
      });

      columns.forEach((column, index) => {
        const header = column.caption || column.dataField || '';
        let maxLength = header.length;

        persons.forEach(person => {
          const value = this.getCellValueForExport(person, column);

          if (value != null) {
            maxLength = Math.max(
              maxLength,
              String(value).length
            );
          }
        });

        worksheet.getColumn(index + 1).width =
          Math.min(maxLength + 8, 80);
      });
    });
  }

  private getCellValueForExport(
    row: PersonTableRowModel,
    column: TableColumn
  ): any {
    if (column.calculateDisplayValue) {
      return column.calculateDisplayValue(row);
    }

    if (column.dataField) {
      return row[column.dataField as keyof PersonTableRowModel];
    }

    return '';
  }

  private getSafeSheetName(name: string): string {
    const invalidCharacters = /[\\/*?:[\]]/g;

    const safeName = name
      .replace(invalidCharacters, '')
      .substring(0, 31);

    return safeName || 'Sheet';
  }

  private getColumnLetter(columnNumber: number): string {
    let result = '';

    while (columnNumber > 0) {
      const remainder = (columnNumber - 1) % 26;

      result = String.fromCharCode(65 + remainder) + result;

      columnNumber = Math.floor(
        (columnNumber - 1) / 26
      );
    }

    return result;
  }
}
